import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Alert, Box, CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import { useSpecificationStateStore } from "./specificationState";
import {
  useGetConditionsFromExp,
  useGetWorkOrdersFromExp,
} from "../../../hooks/queries/useSpecQueries";
import {
  useAddReplicates,
  useModifyCondition,
} from "../../../hooks/mutations/useSpecMutations";
import client from "../../../api";
import { QueryKey } from "../../../utils/api";
import ModifyConditionsModal from "./ModifyConditionsModal";
import CancelWorkOrderGroupModal from "./CancelWorkOrderGroupModal";
import ConditionsList from "./ConditionsList";
import StatusNavigation, { StatusSection } from "./StatusNavigation";
import { useConditionGroups, useConditionValidations } from "./statusState";

/** URL search param used to persist the active StatusView section. */
const CONDITION_GROUP_PARAM = "condition-group";
const VALID_SECTIONS: StatusSection[] = ["wip", "staged", "committed"];

const parseSectionParam = (value: string | null): StatusSection =>
  value && VALID_SECTIONS.includes(value as StatusSection)
    ? (value as StatusSection)
    : "wip";

const Container = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  padding: theme.spacing(3),
  backgroundColor: "white",
  boxSizing: "border-box",
  minWidth: 0,
}));

const Columns = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3),
  flex: 1,
  flexWrap: "wrap",
  minWidth: 0,
}));

type StatusViewProps = {
  /**
   * Set of cell_condition_ids that already belong to a submitted (or
   * in-modification) work order group. Lifted into Specification.tsx so
   * GridView and StatusView share the same derivation.
   */
  submittedConditionIds: Set<number>;
};

const StatusView = ({ submittedConditionIds }: StatusViewProps) => {
  const { exp_id } = useParams();
  const queryClient = useQueryClient();
  const {
    data: workOrders,
    isLoading: isWorkOrdersLoading,
    error: workOrdersError,
  } = useGetWorkOrdersFromExp();
  const {
    data: conditions,
    isLoading: isConditionsLoading,
    error: conditionsError,
  } = useGetConditionsFromExp();
  const activeModifyConditionId = useSpecificationStateStore(
    (state) => state.activeModifyConditionId
  );
  const modifyStatus = useSpecificationStateStore(
    (state) => state.modifyStatus
  );
  const setModifyState = useSpecificationStateStore(
    (state) => state.setModifyState
  );
  const setSuccessMessage = useSpecificationStateStore(
    (state) => state.setSuccessMessage
  );
  const setErrorMessage = useSpecificationStateStore(
    (state) => state.setErrorMessage
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const activeSection = parseSectionParam(
    searchParams.get(CONDITION_GROUP_PARAM)
  );
  const [search, setSearch] = useState("");
  const [selectedNotSubmittedIds, setSelectedNotSubmittedIds] = useState<
    number[]
  >([]);
  const [selectedStagedIds, setSelectedStagedIds] = useState<number[]>([]);
  const [isReplicateModalOpen, setIsReplicateModalOpen] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [cancelGroupId, setCancelGroupId] = useState<string | null>(null);
  const [isCancelPending, setIsCancelPending] = useState(false);

  const { submittedGroups, notSubmitted, staged, committedPlus, statusCounts } =
    useConditionGroups(conditions, workOrders, submittedConditionIds);

  const { isValidationsLoading, conditionHasErrors } = useConditionValidations(
    conditions,
    submittedConditionIds
  );

  const addReplicatesMutation = useAddReplicates();
  const modifyConditionMutation = useModifyCondition();

  // Prune selections that no longer exist in their respective buckets.
  useEffect(() => {
    setSelectedNotSubmittedIds((prev) =>
      prev.filter((id) =>
        notSubmitted.some(({ condition }) => condition.cell_condition_id === id)
      )
    );
  }, [notSubmitted]);

  // Staged and committed+ use the same add-replicates workflow. Selection is
  // cleared before moving between their separate list views. Committed+ also
  // includes conditions belonging to submitted work-order groups, which are
  // selectable for add-replicates too.
  const stagedAndCommitted = useMemo(() => {
    const entries = [...staged, ...committedPlus];
    const seen = new Set(
      entries.map(({ condition }) => condition.cell_condition_id)
    );
    submittedGroups.forEach((group) => {
      group.entries.forEach((entry) => {
        if (!seen.has(entry.condition.cell_condition_id)) {
          seen.add(entry.condition.cell_condition_id);
          entries.push(entry);
        }
      });
    });
    return entries;
  }, [staged, committedPlus, submittedGroups]);

  useEffect(() => {
    setSelectedStagedIds((prev) =>
      prev.filter((id) =>
        stagedAndCommitted.some(
          ({ condition }) => condition.cell_condition_id === id
        )
      )
    );
  }, [stagedAndCommitted]);

  const selectedNotSubmittedConditions = useMemo(() => {
    const selectedIds = new Set(selectedNotSubmittedIds);
    return notSubmitted
      .filter(({ condition }) => selectedIds.has(condition.cell_condition_id))
      .map(({ condition }) => condition);
  }, [notSubmitted, selectedNotSubmittedIds]);

  const selectedStagedConditions = useMemo(() => {
    const selectedIds = new Set(selectedStagedIds);
    return stagedAndCommitted
      .filter(({ condition }) => selectedIds.has(condition.cell_condition_id))
      .map(({ condition }) => condition);
  }, [stagedAndCommitted, selectedStagedIds]);

  const conditionName = (conditionId: number) =>
    conditions?.find((condition) => condition.cell_condition_id === conditionId)
      ?.name ?? `condition ${conditionId}`;

  const invalidateModificationQueries = () =>
    queryClient.invalidateQueries({
      queryKey: [QueryKey.WorkOrders, exp_id, "modify"],
    });

  const handleModifyCondition = async (conditionId: number) => {
    if (!exp_id || modifyConditionMutation.isPending) {
      return;
    }
    try {
      const modification = await modifyConditionMutation.mutateAsync({
        conditionId,
        status: "modifying",
      });
      setModifyState(modification.cell_condition_id, modification.status);
      await invalidateModificationQueries();
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : `Failed to modify ${conditionName(conditionId)}.`
      );
    }
  };

  const handleCancelModifyCondition = async (conditionId: number) => {
    if (!exp_id || modifyConditionMutation.isPending) {
      return;
    }
    try {
      await modifyConditionMutation.mutateAsync({
        conditionId,
        status: "cancelled",
      });
      setModifyState(null, null);
      await invalidateModificationQueries();
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Conditions, exp_id],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKey.Items] });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.WorkOrders, exp_id],
      });
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : `Failed to cancel changes to ${conditionName(conditionId)}.`
      );
    }
  };

  const handleResubmitCondition = async (conditionId: number) => {
    if (!exp_id || conditionId !== activeModifyConditionId) {
      return;
    }
    try {
      const modification = await modifyConditionMutation.mutateAsync({
        conditionId,
        status: "submitted",
      });
      setModifyState(modification.cell_condition_id, modification.status);
      setSuccessMessage(
        `Resubmission queued for ${conditionName(conditionId)}.`
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : `Failed to resubmit ${conditionName(conditionId)}.`
      );
    }
  };

  const handleConfirmCancel = async () => {
    if (!exp_id || !cancelGroupId || isCancelPending) {
      return;
    }

    const groupId = cancelGroupId;
    setIsCancelPending(true);
    try {
      const response = await client.post(
        `meta/experiments/${exp_id}/conditions/modify`,
        {
          group_id: groupId,
          status: "deleted",
        }
      );
      if (
        response?.data?.status !== "deleted" ||
        String(response?.data?.group_id) !== groupId
      ) {
        throw new Error(
          "The server returned an unexpected cancellation response."
        );
      }

      void Promise.allSettled([
        queryClient.invalidateQueries({
          queryKey: [QueryKey.WorkOrders, exp_id, "modify"],
        }),
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Conditions, exp_id],
        }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.Items] }),
        queryClient.invalidateQueries({
          queryKey: [QueryKey.WorkOrders, exp_id],
        }),
      ]);
      setCancelGroupId(null);
      setSuccessMessage(
        `Cancellation requested for work order group ${groupId}. Tickets may take a few minutes to show as canceled.`
      );
    } catch (error) {
      const payload =
        typeof error === "object" && error !== null && "payload" in error
          ? (error as { payload?: unknown }).payload
          : undefined;
      const payloadMessage =
        typeof payload === "object" && payload !== null
          ? (
              payload as {
                message?: unknown;
                detail?: unknown;
                description?: unknown;
                title?: unknown;
              }
            ).message ??
            (payload as { detail?: unknown }).detail ??
            (payload as { description?: unknown }).description ??
            (payload as { title?: unknown }).title
          : undefined;
      const message =
        payloadMessage !== undefined
          ? String(payloadMessage)
          : error instanceof Error && error.message
          ? error.message
          : "Please try again.";
      setErrorMessage(
        `Failed to cancel work order group ${groupId}: ${message}`
      );
    } finally {
      setIsCancelPending(false);
    }
  };

  // --------------------------------------------------------------------------
  // Search filtering + selectable id derivation
  // --------------------------------------------------------------------------

  const filteredNotSubmitted = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return notSubmitted;
    }
    return notSubmitted.filter(({ condition }) =>
      condition.name.toLowerCase().includes(query)
    );
  }, [notSubmitted, search]);

  // Filter out conditions with errors from being selectable. Also disable all
  // conditions until validations are loaded.
  const selectableNotSubmittedIds = useMemo(() => {
    if (isValidationsLoading) {
      return [];
    }
    return filteredNotSubmitted
      .filter(({ condition }) => !conditionHasErrors(condition))
      .map(({ condition }) => condition.cell_condition_id);
  }, [filteredNotSubmitted, conditionHasErrors, isValidationsLoading]);

  const areAllFilteredNotSubmittedSelected = useMemo(
    () =>
      selectableNotSubmittedIds.length > 0 &&
      selectableNotSubmittedIds.every((id) =>
        selectedNotSubmittedIds.includes(id)
      ),
    [selectableNotSubmittedIds, selectedNotSubmittedIds]
  );

  const filteredSubmittedGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return submittedGroups;
    }
    return submittedGroups
      .map((group) => ({
        ...group,
        entries: group.entries.filter(({ condition }) =>
          condition.name.toLowerCase().includes(query)
        ),
      }))
      .filter((group) => group.entries.length > 0);
  }, [submittedGroups, search]);

  const filteredStaged = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return staged;
    }
    return staged.filter(({ condition }) =>
      condition.name.toLowerCase().includes(query)
    );
  }, [staged, search]);

  const filteredCommittedPlus = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return committedPlus;
    }
    return committedPlus.filter(({ condition }) =>
      condition.name.toLowerCase().includes(query)
    );
  }, [committedPlus, search]);

  // Unique conditions belonging to the (filtered) submitted work-order groups.
  // These render below the plain committed conditions and are selectable for
  // the add-replicates flow as well.
  const submittedGroupConditions = useMemo(() => {
    const byId = new Map<number, FormspecCondition>();
    filteredSubmittedGroups.forEach((group) => {
      group.entries.forEach(({ condition }) => {
        if (!byId.has(condition.cell_condition_id)) {
          byId.set(condition.cell_condition_id, condition);
        }
      });
    });
    return Array.from(byId.values());
  }, [filteredSubmittedGroups]);

  // Select-all only toggles conditions visible in the active filtered list.
  const selectableStagedIds = useMemo(
    () => filteredStaged.map(({ condition }) => condition.cell_condition_id),
    [filteredStaged]
  );

  const selectableCommittedPlusIds = useMemo(() => {
    const ids = filteredCommittedPlus.map(
      ({ condition }) => condition.cell_condition_id
    );
    const seen = new Set(ids);
    submittedGroupConditions.forEach((condition) => {
      if (!seen.has(condition.cell_condition_id)) {
        seen.add(condition.cell_condition_id);
        ids.push(condition.cell_condition_id);
      }
    });
    return ids;
  }, [filteredCommittedPlus, submittedGroupConditions]);

  const areAllFilteredStagedSelected = useMemo(
    () =>
      selectableStagedIds.length > 0 &&
      selectableStagedIds.every((id) => selectedStagedIds.includes(id)),
    [selectableStagedIds, selectedStagedIds]
  );

  const areAllFilteredCommittedPlusSelected = useMemo(
    () =>
      selectableCommittedPlusIds.length > 0 &&
      selectableCommittedPlusIds.every((id) => selectedStagedIds.includes(id)),
    [selectableCommittedPlusIds, selectedStagedIds]
  );

  // --------------------------------------------------------------------------
  // Selection handlers
  // --------------------------------------------------------------------------

  const toggleStagedSelectionForIds = (ids: number[], allSelected: boolean) => {
    if (ids.length === 0) {
      return;
    }
    setSelectedStagedIds((prev) => {
      if (allSelected) {
        return prev.filter((id) => !ids.includes(id));
      }
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const handleToggleAllStaged = () =>
    toggleStagedSelectionForIds(
      selectableStagedIds,
      areAllFilteredStagedSelected
    );

  const handleToggleAllCommittedPlus = () =>
    toggleStagedSelectionForIds(
      selectableCommittedPlusIds,
      areAllFilteredCommittedPlusSelected
    );

  const handleToggleStagedCondition = (conditionId: number) => {
    setSelectedStagedIds((prev) =>
      prev.includes(conditionId)
        ? prev.filter((id) => id !== conditionId)
        : [...prev, conditionId]
    );
  };

  const handleToggleCondition = (conditionId: number) => {
    setSelectedNotSubmittedIds((prev) =>
      prev.includes(conditionId)
        ? prev.filter((id) => id !== conditionId)
        : [...prev, conditionId]
    );
  };

  const handleToggleAllNotSubmitted = () => {
    if (selectableNotSubmittedIds.length === 0) {
      return;
    }
    setSelectedNotSubmittedIds((prev) => {
      if (areAllFilteredNotSubmittedSelected) {
        return prev.filter((id) => !selectableNotSubmittedIds.includes(id));
      }
      const next = new Set(prev);
      selectableNotSubmittedIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const handleSectionChange = (section: StatusSection) => {
    if (section === activeSection) {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(CONDITION_GROUP_PARAM, section);
    setSearchParams(nextParams, { replace: true });
    setSelectedNotSubmittedIds([]);
    setSelectedStagedIds([]);
  };

  // --------------------------------------------------------------------------
  // Modal handlers
  // --------------------------------------------------------------------------

  const handleAddReplicateClick = () => {
    if (selectedStagedConditions.length === 0) {
      return;
    }
    setIsReplicateModalOpen(true);
  };

  /**
   * Applies each condition's replicate delta. Resolves when every update
   * succeeds (modal closes); throws with a user-facing message if any fail so
   * the modal stays open and can display it. The same message is also pushed
   * to the page-level error banner.
   */
  const handleConfirmAddReplicates = async (results: AddReplicatesResult[]) => {
    if (results.length === 0) {
      // Every row is already at its current count; nothing to change.
      setIsReplicateModalOpen(false);
      return;
    }

    const conditionNames = new Map(
      selectedStagedConditions.map((condition) => [
        condition.cell_condition_id,
        condition.name,
      ])
    );

    const settled = await Promise.allSettled(
      results.map((result) => addReplicatesMutation.mutateAsync(result))
    );

    const failures: { name: string; reason: string }[] = [];
    let addedReplicates = 0;
    let updatedConditions = 0;
    settled.forEach((outcome, index) => {
      const result = results[index];
      if (outcome.status === "fulfilled") {
        addedReplicates += result.numberOfReplicates;
        updatedConditions += 1;
        return;
      }
      console.error("Error adding replicates:", outcome.reason);
      failures.push({
        name:
          conditionNames.get(result.conditionId) ??
          `condition ${result.conditionId}`,
        reason:
          outcome.reason instanceof Error && outcome.reason.message
            ? outcome.reason.message
            : "Unknown error",
      });
    });

    const successSummary = `Added ${addedReplicates} replicate(s) across ${updatedConditions} condition(s).`;

    if (failures.length === 0) {
      setIsReplicateModalOpen(false);
      setSelectedStagedIds([]);
      setSuccessMessage(successSummary);
      return;
    }

    const failureDetails = failures
      .map(({ name, reason }) => `${name}: ${reason}`)
      .join("; ");
    const message =
      updatedConditions > 0
        ? `${successSummary} Failed to add replicates for ${failures.length} condition(s) — ${failureDetails}`
        : `Failed to add replicates — ${failureDetails}`;
    setErrorMessage(message);
    throw new Error(message);
  };

  const handleSubmitModalComplete = () => {
    setIsSubmitModalOpen(false);
    setSelectedNotSubmittedIds([]);
    setSuccessMessage("Cells created successfully!");
  };

  const handleSubmitModalError = (message: string) => {
    setErrorMessage(message);
  };

  const isReadOnly = activeModifyConditionId !== null;
  const isResubmitPending =
    modifyConditionMutation.isPending || modifyStatus === "queued";
  const activeEntries =
    activeSection === "wip"
      ? filteredNotSubmitted
      : activeSection === "staged"
      ? filteredStaged
      : filteredCommittedPlus;
  const activeTotalEntries =
    activeSection === "wip"
      ? notSubmitted.length
      : activeSection === "staged"
      ? staged.length
      : committedPlus.length +
        submittedGroups.reduce(
          (total, group) => total + group.entries.length,
          0
        );
  const activeSelectedIds =
    activeSection === "wip" ? selectedNotSubmittedIds : selectedStagedIds;
  const activeSelectableIds =
    activeSection === "wip"
      ? selectableNotSubmittedIds
      : activeSection === "staged"
      ? selectableStagedIds
      : selectableCommittedPlusIds;
  const areAllActiveSelected =
    activeSection === "wip"
      ? areAllFilteredNotSubmittedSelected
      : activeSection === "staged"
      ? areAllFilteredStagedSelected
      : areAllFilteredCommittedPlusSelected;
  const handleToggleActive =
    activeSection === "wip"
      ? handleToggleCondition
      : handleToggleStagedCondition;
  const handleToggleAllActive =
    activeSection === "wip"
      ? handleToggleAllNotSubmitted
      : activeSection === "staged"
      ? handleToggleAllStaged
      : handleToggleAllCommittedPlus;
  const isCtaDisabled =
    activeSection === "wip"
      ? selectedNotSubmittedConditions.length === 0 || isReadOnly
      : selectedStagedConditions.length === 0 ||
        addReplicatesMutation.isPending ||
        isReadOnly;

  if (isWorkOrdersLoading || isConditionsLoading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#fafafa",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (workOrdersError || conditionsError) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#fafafa",
          padding: 3,
        }}
      >
        <Alert severity="error">
          Unable to load condition statuses. Please refresh the page.
        </Alert>
      </Box>
    );
  }

  return (
    <Container>
      <Columns>
        <StatusNavigation
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
          counts={{
            wip: statusCounts.wip,
            staged: statusCounts.staged,
            committed: statusCounts.committed,
          }}
        />
        <ConditionsList
          activeSection={activeSection}
          search={search}
          onSearchChange={setSearch}
          entries={activeEntries}
          totalEntries={activeTotalEntries}
          filteredSubmittedGroups={filteredSubmittedGroups}
          selectedIds={activeSelectedIds}
          selectableIds={activeSelectableIds}
          areAllSelected={areAllActiveSelected}
          onToggle={handleToggleActive}
          onToggleAll={handleToggleAllActive}
          onCreateCells={() => setIsSubmitModalOpen(true)}
          onAddReplicates={handleAddReplicateClick}
          isCtaDisabled={isCtaDisabled}
          isReadOnly={isReadOnly}
          isValidationsLoading={isValidationsLoading}
          conditionHasErrors={conditionHasErrors}
          activeModifyConditionId={activeModifyConditionId}
          modificationStatus={modifyStatus}
          isCommitLoading={isResubmitPending}
          onModifyCondition={handleModifyCondition}
          onCancelModifyCondition={handleCancelModifyCondition}
          onResubmitCondition={handleResubmitCondition}
          onDeleteSubmission={(groupId) => setCancelGroupId(groupId)}
        />
      </Columns>

      <ModifyConditionsModal
        mode="add-replicates"
        open={isReplicateModalOpen}
        onClose={() => setIsReplicateModalOpen(false)}
        conditions={selectedStagedConditions}
        onConfirm={handleConfirmAddReplicates}
      />
      <ModifyConditionsModal
        mode="submit"
        open={isSubmitModalOpen}
        onClose={() => setIsSubmitModalOpen(false)}
        conditions={selectedNotSubmittedConditions}
        onSubmit={handleSubmitModalComplete}
        onError={handleSubmitModalError}
      />
      <CancelWorkOrderGroupModal
        open={Boolean(cancelGroupId)}
        groupId={cancelGroupId}
        onClose={() => setCancelGroupId(null)}
        onConfirm={handleConfirmCancel}
        isPending={isCancelPending}
      />
    </Container>
  );
};

export default StatusView;
